<template>
  <div>
    <Layout ref="layoutRef" :selectedOption="2" />
    <div class="mb-3">
      <h4>Reporte de Atenciones por Tipo</h4>
    </div>
    <hr>

    <div class="mb-3 d-flex flex-wrap gap-2">
      <button
        v-for="atajo in atajos" :key="atajo.key"
        class="btn btn-sm" :class="atajo.clase"
        @click="aplicarAtajo(atajo)"
      >{{ atajo.label }}</button>
    </div>

    <div class="input-group mb-3">
      <span class="input-group-text">Desde</span>
      <input type="date" v-model="fechaDesde" class="form-control">
      <span class="input-group-text">Hasta</span>
      <input type="date" v-model="fechaHasta" class="form-control">
      <button
        class="btn btn-dark"
        @click="generarReporte"
        :disabled="!fechaDesde || !fechaHasta || cargando"
      >
        <span v-if="cargando" class="spinner-border spinner-border-sm me-1"></span>
        Consultar
      </button>
      <button
        class="btn btn-success"
        @click="exportToCSV"
        :disabled="!atencionesPorTipo.length"
      >
        <span class="material-symbols-outlined align-middle" style="font-size:16px">download</span>
        CSV
      </button>
    </div>

    <div v-if="atencionesPorTipo.length">

      <!-- KPIs -->
      <div class="row g-3 mb-4">
        <div class="col-6 col-md-4">
          <div class="kpi-card">
            <div class="kpi-label">Total atenciones</div>
            <div class="kpi-value">{{ totalAtenciones.toLocaleString() }}</div>
          </div>
        </div>
        <div class="col-6 col-md-4">
          <div class="kpi-card">
            <div class="kpi-label">Tipos registrados</div>
            <div class="kpi-value">{{ atencionesPorTipo.length }}</div>
          </div>
        </div>
        <div class="col-12 col-md-4">
          <div class="kpi-card">
            <div class="kpi-label">Tipo más frecuente</div>
            <div class="kpi-value kpi-value--sm">{{ tipoTop }}</div>
            <div class="kpi-sub">{{ porcentaje(maxAtenciones) }}% del total</div>
          </div>
        </div>
      </div>

      <!-- Tabs -->
      <div class="tabs">
        <button class="tablink" :class="{ 'active-tab': vistaActiva === 'tabla' }" @click="vistaActiva = 'tabla'">
          <span class="material-symbols-outlined" style="font-size:17px">table_view</span>
          Tabla
        </button>
        <button class="tablink" :class="{ 'active-tab': vistaActiva === 'grafico' }" @click="vistaActiva = 'grafico'">
          <span class="material-symbols-outlined" style="font-size:17px">pie_chart</span>
          Gráfico
        </button>
      </div>

      <!-- ── TABLA ── -->
      <div v-if="vistaActiva === 'tabla'" class="mt-3">
        <table class="table table-bordered table-hover table-sm">
          <thead class="table-dark">
            <tr>
              <th class="sortable" @click="ordenar('nombre')">
                Tipo
                <span class="material-symbols-outlined sort-icon">
                  {{ sortKey === 'nombre' ? (sortDir === 'asc' ? 'arrow_upward' : 'arrow_downward') : 'unfold_more' }}
                </span>
              </th>
              <th class="text-end sortable" @click="ordenar('atenciones')">
                Atenciones
                <span class="material-symbols-outlined sort-icon">
                  {{ sortKey === 'atenciones' ? (sortDir === 'asc' ? 'arrow_upward' : 'arrow_downward') : 'unfold_more' }}
                </span>
              </th>
              <th class="text-end">%</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in filasOrdenadas" :key="index">
              <td>
                <span class="dot" :style="{ background: colores[index % colores.length] }"></span>
                {{ item[0] }}
              </td>
              <td class="text-end">{{ item[1] }}</td>
              <td class="text-end">
                <div class="d-flex align-items-center justify-content-end gap-2">
                  <div class="pct-bar-wrap">
                    <div
                      class="pct-bar"
                      :style="{ width: porcentaje(item[1]) + '%', background: colores[index % colores.length] }"
                    ></div>
                  </div>
                  {{ porcentaje(item[1]) }}%
                </div>
              </td>
            </tr>
          </tbody>
          <tfoot>
            <tr class="table-secondary fw-bold">
              <td>Total</td>
              <td class="text-end">{{ totalAtenciones }}</td>
              <td class="text-end">100%</td>
            </tr>
          </tfoot>
        </table>
      </div>

      <!-- ── GRÁFICO ── -->
      <div v-if="vistaActiva === 'grafico'" class="mt-3">
        <apexchart
          type="pie"
          :options="chartOptions"
          :series="chartSeries"
          height="440"
        />
      </div>

    </div>

    <div v-else-if="consultado" class="alert alert-info mt-3">
      No hay datos para el período seleccionado.
    </div>
    <Footer />
  </div>
</template>

<script>
import Layout        from '@/components/Layout.vue'
import Footer        from '@/components/Footer.vue'
import VueApexCharts from 'vue3-apexcharts'
import api           from '@/services/api'
import {
  getToday, getThisWeek, getThisMonth,
  getThisQuarter, getThisSemester, getThisYear,
} from '@/modules/queryDateUtils.js'

// Paleta consistente entre tabla y gráfico
const PALETA = [
  '#0d6efd', '#20c997', '#fd7e14', '#6f42c1',
  '#d63384', '#0dcaf0', '#ffc107', '#198754',
  '#dc3545', '#6c757d',
]

export default {
  name: 'ReporteAtencionesPorTipo',
  components: { Layout, Footer, apexchart: VueApexCharts },
  data() {
    return {
      fechaDesde:        '',
      fechaHasta:        '',
      atencionesPorTipo: [],
      vistaActiva:       'tabla',
      cargando:          false,
      consultado:        false,
      sortKey:           'atenciones',
      sortDir:           'desc',
      colores:           PALETA,
      atajos: [
        { key: 'hoy',       label: 'Hoy',             clase: 'btn-outline-primary',   fn: getToday        },
        { key: 'semana',    label: 'Semana actual',    clase: 'btn-outline-danger',    fn: getThisWeek     },
        { key: 'mes',       label: 'Mes actual',       clase: 'btn-outline-secondary', fn: getThisMonth    },
        { key: 'trimestre', label: 'Trimestre actual', clase: 'btn-outline-success',   fn: getThisQuarter  },
        { key: 'semestre',  label: 'Semestre actual',  clase: 'btn-outline-info',      fn: getThisSemester },
        { key: 'anio',      label: 'Año actual',       clase: 'btn-outline-warning',   fn: getThisYear     },
      ],
    }
  },
  computed: {
    totalAtenciones() {
      return this.atencionesPorTipo.reduce((s, i) => s + i[1], 0)
    },
    tipoTop() {
      if (!this.atencionesPorTipo.length) return '—'
      return [...this.atencionesPorTipo].sort((a, b) => b[1] - a[1])[0][0]
    },
    maxAtenciones() {
      if (!this.atencionesPorTipo.length) return 0
      return Math.max(...this.atencionesPorTipo.map(i => i[1]))
    },

    // Tabla con ordenamiento (sin paginación — tipos son pocos)
    filasOrdenadas() {
      return [...this.atencionesPorTipo].sort((a, b) => {
        const av = this.sortKey === 'nombre' ? a[0] : a[1]
        const bv = this.sortKey === 'nombre' ? b[0] : b[1]
        if (av < bv) return this.sortDir === 'asc' ? -1 : 1
        if (av > bv) return this.sortDir === 'asc' ? 1 : -1
        return 0
      })
    },

    // Gráfico — datos siempre ordenados desc para que el pie empiece por el mayor
    datosGrafico() {
      return [...this.atencionesPorTipo].sort((a, b) => b[1] - a[1])
    },
    chartOptions() {
      return {
        chart:  { toolbar: { show: false }, fontFamily: 'inherit' },
        labels: this.datosGrafico.map(i => i[0] || 'Desconocido'),
        colors: PALETA,
        tooltip: {
          y: { formatter: v => `${v.toLocaleString()} atenciones (${((v / this.totalAtenciones) * 100).toFixed(1)}%)` },
        },
        legend: {
          position: 'bottom',
          fontSize:  '13px',
          formatter: (label, opts) => {
            const v = opts.w.globals.series[opts.seriesIndex]
            return `${label} — ${v.toLocaleString()}`
          },
        },
        dataLabels: {
          formatter: (val) => `${val.toFixed(1)}%`,
        },
        responsive: [{
          breakpoint: 480,
          options: { chart: { width: 300 }, legend: { position: 'bottom' } },
        }],
      }
    },
    chartSeries() {
      return this.datosGrafico.map(i => i[1])
    },
  },
  mounted() {
    this.$refs.layoutRef.toggleProcesos()
    this.$refs.layoutRef.toggleReportes()
    this.$refs.layoutRef.toggleParametros()
    this.$refs.layoutRef.toggleAyuda()
  },
  methods: {
    aplicarAtajo(atajo) {
      const { fechaDesde, fechaHasta } = atajo.fn()
      this.fechaDesde = fechaDesde
      this.fechaHasta = fechaHasta
      this.generarReporte()
    },
async generarReporte() {
  if (!this.fechaDesde || !this.fechaHasta) return
  this.cargando   = true
  this.consultado = false
  try {
    const idEmpresa = window.sessionStorage.getItem('idEmpresa')
    const { data }  = await api.get('/vwatencions/por-fecha', {
      params: { idEmpresa, fechaDesde: this.fechaDesde, fechaHasta: this.fechaHasta },
    })

    const mapa = {}
    for (const ate of (data ?? [])) {
      const tipo = ate.tipo ?? 'SIN TIPO'
      mapa[tipo] = (mapa[tipo] ?? 0) + 1
    }

    this.atencionesPorTipo = Object.entries(mapa)
      .map(([nombre, count]) => [nombre, count])
      .sort((a, b) => b[1] - a[1])

  } catch (error) {
    console.error('Error al obtener los datos:', error)
    this.atencionesPorTipo = []
  } finally {
    this.cargando   = false
    this.consultado = true
  }
},
    ordenar(key) {
      if (this.sortKey === key) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortKey = key
        this.sortDir = key === 'atenciones' ? 'desc' : 'asc'
      }
    },
    porcentaje(valor) {
      if (!this.totalAtenciones) return 0
      return ((valor / this.totalAtenciones) * 100).toFixed(1)
    },
    exportToCSV() {
      const filas = [
        ['Tipo', 'Número de Atenciones', '%'],
        ...this.atencionesPorTipo.map(item => [
          item[0], item[1], `${this.porcentaje(item[1])}%`,
        ]),
        ['Total', this.totalAtenciones, '100%'],
      ]
      const csv  = filas.map(f => f.join(',')).join('\n')
      const blob = new Blob([csv], { type: 'text/csv' })
      const url  = URL.createObjectURL(blob)
      const a    = Object.assign(document.createElement('a'), {
        href: url, download: 'Reporte_Atenciones_Por_Tipo.csv',
      })
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    },
  },
}
</script>

<style scoped>
.kpi-card {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 14px 18px;
  text-align: center;
}
.kpi-label {
  font-size: 0.75rem;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: .04em;
  margin-bottom: 4px;
}
.kpi-value {
  font-size: 1.6rem;
  font-weight: 700;
  color: #0d6efd;
  line-height: 1.1;
}
.kpi-value--sm { font-size: 0.95rem; font-weight: 600; }
.kpi-sub       { font-size: 0.8rem; color: #6c757d; margin-top: 2px; }

.tabs { display: flex; gap: 4px; border-bottom: 2px solid #dee2e6; }
.tablink {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 8px 18px; border: none;
  border-bottom: 3px solid transparent; margin-bottom: -2px;
  background: transparent; color: #6c757d;
  font-size: 0.875rem; font-weight: 500; cursor: pointer;
  border-radius: 6px 6px 0 0; transition: all 0.2s ease;
}
.tablink:hover { color: #0d6efd; background: #f0f4ff; }
.active-tab    { color: #0d6efd; border-bottom-color: #0d6efd; background: #fff; font-weight: 600; }

.sortable       { cursor: pointer; user-select: none; white-space: nowrap; }
.sortable:hover { background: #495057; }
.sort-icon      { font-size: 14px; vertical-align: middle; opacity: .7; }

/* Dot de color sincronizado con el gráfico */
.dot {
  display: inline-block;
  width: 10px; height: 10px;
  border-radius: 50%;
  margin-right: 6px;
  vertical-align: middle;
  flex-shrink: 0;
}

.pct-bar-wrap {
  width: 60px; height: 6px;
  background: #e9ecef; border-radius: 3px; overflow: hidden;
}
.pct-bar {
  height: 100%; border-radius: 3px; transition: width 0.4s ease;
}
</style>